import { Component, Input, Optional, Self, ElementRef, ViewChild, OnInit } from '@angular/core';
import { ControlValueAccessor, NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'lib-textarea',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './textarea.component.html',
  styleUrl: './textarea.component.scss',
})
export class TextareaComponent implements ControlValueAccessor, OnInit {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() errorMessage = '';
  @Input() rows = 4;

  @ViewChild('textareaElement', { static: false }) textareaElement?: ElementRef<HTMLTextAreaElement>;

  textareaId = `textarea-${Math.random().toString(36).substring(2, 9)}`;

  value = '';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (_value: string) => {
    // ControlValueAccessor interface requirement
  };
  onTouched = () => {
    // ControlValueAccessor interface requirement
  };

  // ControlValueAccessor pattern requires NgControl injection via constructor
  // This is the standard Angular pattern for custom form controls
  // eslint-disable-next-line
  constructor(@Optional() @Self() public ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    // If using reactive forms, sync error message from form control
    if (this.ngControl?.errors) {
      // Error handling will be done via errorMessage input
    }
  }

  get hasError(): boolean {
    if (this.errorMessage) {
      return true;
    }
    if (this.ngControl?.errors && this.ngControl?.touched) {
      return true;
    }
    return false;
  }

  get errorText(): string {
    if (this.errorMessage) {
      return this.errorMessage;
    }
    if (this.ngControl?.errors && this.ngControl?.touched) {
      const errors = this.ngControl.errors;
      if (errors['required']) {
        return 'This field is required';
      }
      if (errors['minlength']) {
        return `Minimum length is ${errors['minlength'].requiredLength}`;
      }
      if (errors['maxlength']) {
        return `Maximum length is ${errors['maxlength'].requiredLength}`;
      }
      return 'Invalid value';
    }
    return '';
  }

  onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.value = target.value;
    this.onChange(this.value);
  }

  onBlur(): void {
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
    if (this.textareaElement) {
      this.textareaElement.nativeElement.value = this.value;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

